import type {
  AgentRunDisplayStatus,
  AgentRunFailureCode,
  AgentRunListItem,
  AgentRunTests,
} from '@fos/shared/contracts/agents';
import {
  AGENT_RUN_DISPLAY_STATUS_LABELS,
  AGENT_RUN_DISPLAY_STATUS_TONES,
  AGENT_RUN_FAILURE_LABELS,
} from '@fos/shared/labels';
import { NBSP } from '@fos/shared/money';
import { formatDuration, formatMoney } from '../../lib/format';
import type { Tone } from '../../ui';

/** Formatowanie przebiegów (Figma 11: „14 min”, „40 s”, „1,84 zł”, „Przerwany: limit czasu”). */

/** Czas pracy runu: „40 s”, „14 min”, „1 h 05 min”. */
export function formatRunDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))}${NBSP}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}${NBSP}min`;
  return formatDuration(seconds);
}

/** Koszt runu z 4 miejsc do groszy: „1,84 zł”; poniżej grosza „< 0,01 zł”; brak albo 0 (koszt nieznany) = „—”. */
export function formatRunCost(cost: string | undefined | null): string {
  if (cost === undefined || cost === null) return '—';
  const n = Number(cost);
  if (!Number.isFinite(n) || n === 0) return '—';
  if (n > 0 && n < 0.005) return `<${NBSP}${formatMoney('0.01')}`;
  return formatMoney(n.toFixed(2));
}

/** Etykieta statusu do listy: błąd z kodem pokazuje powód („Przerwany: limit czasu”). */
export function runStatusLabel(run: {
  displayStatus: AgentRunDisplayStatus;
  failureCode: AgentRunFailureCode | null;
}): string {
  if (run.displayStatus === 'failed' && run.failureCode) return AGENT_RUN_FAILURE_LABELS[run.failureCode];
  return AGENT_RUN_DISPLAY_STATUS_LABELS[run.displayStatus];
}

export function runStatusTone(run: { displayStatus: AgentRunDisplayStatus }): Tone {
  return AGENT_RUN_DISPLAY_STATUS_TONES[run.displayStatus];
}

/** Nazwa klienta albo projektu pod tytułem („REA”). */
export function runContext(run: Pick<AgentRunListItem, 'client' | 'project'>): string | null {
  return run.client?.name ?? run.project?.name ?? null;
}

/** „testy 12/12” albo „testy 11/12 (1 nie przechodzi)”. */
export function testsLabel(tests: AgentRunTests | null | undefined): string | null {
  if (!tests || tests.passed === null || tests.failed === null) return null;
  const total = tests.passed + tests.failed;
  return tests.failed > 0
    ? `testy ${tests.passed}/${total} (${tests.failed} nie przechodzi)`
    : `testy ${tests.passed}/${total}`;
}

/** Pierwsze 1–2 zdania podsumowania (blok wyniku na karcie zadania, Figma 04). */
export function summaryExcerpt(summary: string | null, maxSentences = 2, maxChars = 280): string | null {
  if (!summary) return null;
  const text = summary.replace(/\s+/g, ' ').trim();
  const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g);
  const picked = sentences ? sentences.slice(0, maxSentences).join('').trim() : text;
  const out = picked || text;
  return out.length > maxChars ? `${out.slice(0, maxChars - 1).trimEnd()}…` : out;
}

/** Ścieżka szczegółów przebiegu (z kotwicą sekcji). */
export function runPath(id: string, section?: 'zmiany' | 'dziennik'): string {
  return `/agenci/${id}${section ? `#${section}` : ''}`;
}

/** Krótki commit: „4f9c2a1”. */
export function shortSha(sha: string | null): string | null {
  return sha ? sha.slice(0, 7) : null;
}
